import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import * as CIActions from 'ApplicationEntry/ducks/ProductsRiders/criticalIllness';
import {isValidPolicyNumber} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {
  getAllRiderCodeUnits,
  getRiderUnits,
  InvalidNewYorkCriticalIllness,
  ValidNewYorkCriticalIllness,
} from 'ApplicationEntry/models/ProductsRiders/CriticalIllness/NewYorkCriticalIllness';
import {CriticalIllnessNYPlanType} from 'ApplicationEntry/models/ProductsRiders/PlanTypes';
import React, {useState} from 'react';
import {useDispatch} from 'react-redux';
import * as ErrorMessages from '../../ErrorMessages';
import '../Ordinary/CriticalIllness.sass';

type CriticalIllnessProps = {
  criticalIllness: ValidNewYorkCriticalIllness | InvalidNewYorkCriticalIllness;
};

const NYCriticalIllness = (props: CriticalIllnessProps) => {
  const dispatch = useDispatch();
  const {
    planType,
    insuredSC,
    premium,
    riderAmounts,
    spouseSC,
    benefitAmount,
    cwa,
    policyNumber,
    replacement,
    riderCodeList,
    section125,
    cancerBenefit,
  } = props.criticalIllness;

  const [planTypeBlurred, setBlurPlanType] = useState(false);
  const [policyNumberBlurred, setPolicyNumberBlurred] = useState(false);
  const showPlanTypeError = planTypeBlurred && planType.key === 'Empty';
  const [planTypeLabel, setPlanTypeLabel] = useState<HTMLLabelElement | null>();
  const [riderLabel, setRiderLabel] = useState<HTMLLabelElement | null>();
  const [smokerLabel, setSmokerLabel] = useState<HTMLLabelElement | null>();

  const showPolicyNumberError = policyNumberBlurred && !isValidPolicyNumber(policyNumber) && replacement;

  return (
    <>
      <div className="critical-illness-section" id="critical-illness-section">
        <form noValidate autoComplete="off" className="critical-illness-section__form">
          <div className="critical-illness-section__form-row">
            <Grid container spacing={4}>
              <Grid item xs={12} md={3}>
                <FormControl
                  error={showPlanTypeError}
                  variant="outlined"
                  className="critical-illness-section__form-text-field"
                >
                  <InputLabel ref={setPlanTypeLabel} id="critical-illness-plantype-label">
                    Plan Type
                  </InputLabel>
                  <Select
                    labelId="critical-illness-label-plantype"
                    labelWidth={planTypeLabel?.clientWidth}
                    id="critical-illness-plantype"
                    value={planType.key === 'Empty' ? '' : planType.value}
                    onBlur={() => setBlurPlanType(true)}
                    onChange={e => {
                      dispatch(CIActions.changePlanType(e.target.value as string));
                      if (e.target.value !== 'FAMILY') {
                        dispatch(CIActions.changeSpouseSC(''));
                      }
                    }}
                    displayEmpty
                  >
                    {CriticalIllnessNYPlanType.map(plan => (
                      <MenuItem value={plan.value} key={plan.value}>
                        {plan.key}
                      </MenuItem>
                    ))}
                  </Select>
                  {showPlanTypeError && <FormHelperText>{ErrorMessages.REQUIRED_MESSAGE}</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl variant="outlined" className="critical-illness-section__form-text-field">
                  <InputLabel ref={setSmokerLabel} id="critical-illness-insuredsc-label">
                    Insured Smoking Code
                  </InputLabel>
                  <Select
                    labelId="critical-illness-label-insuredsc"
                    labelWidth={smokerLabel?.clientWidth}
                    id="critical-illness-insuredsc"
                    value={insuredSC.key === 'Empty' ? '' : insuredSC.value}
                    onChange={e => {
                      dispatch(CIActions.changeInsuredSC(e.target.value as string));
                    }}
                    displayEmpty
                  >
                    <MenuItem value="">&nbsp;&nbsp;</MenuItem>
                    <MenuItem value="Smoker">Smoker</MenuItem>
                    <MenuItem value="Non Smoker">Non Smoker</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl variant="outlined" className="critical-illness-section__form-text-field" disabled={true}>
                  <InputLabel ref={setSmokerLabel} id="critical-illness-spousesc-label">
                    Spouse Smoking Code
                  </InputLabel>
                  <Select
                    labelId="critical-illness-label-spousesc"
                    labelWidth={smokerLabel?.clientWidth}
                    id="critical-illness-spouse-sc"
                    value={spouseSC.key === 'Empty' ? '' : spouseSC.value}
                    onChange={e => dispatch(CIActions.changeSpouseSC(e.target.value as string))}
                    displayEmpty
                    disabled={planType.value === 'FAMILY' ? false : true}
                  >
                    <MenuItem value="">&nbsp;&nbsp;</MenuItem>
                    <MenuItem value="Smoker">Smoker</MenuItem>
                    <MenuItem value="Non Smoker">Non Smoker</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="checkedB"
                      color="secondary"
                      id="critical-illness-cancer-benefit"
                      checked={cancerBenefit}
                      onChange={e => dispatch(CIActions.changeCancerBenefit(!cancerBenefit))}
                    />
                  }
                  label={<Typography>Cancer Benefit</Typography>}
                  className="critical-illness-section__form-text-field"
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="checkedB"
                      color="secondary"
                      id="critical-illness-section-125"
                      checked={section125}
                      onChange={e => dispatch(CIActions.changeSection125(!section125))}
                    />
                  }
                  label={<Typography>Section 125</Typography>}
                  className="critical-illness-section__form-text-field"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  id="critical-illness-premium"
                  label="Premium"
                  variant="outlined"
                  placeholder="Enter Premium"
                  margin="normal"
                  fullWidth
                  className="critical-illness-section__form-text-field"
                  value={premium.value}
                  onChange={e => dispatch(CIActions.changePremium(e.target.value as string))}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  id="critical-illness-cwa"
                  label="CWA"
                  variant="outlined"
                  placeholder="Enter CWA"
                  margin="normal"
                  fullWidth
                  className="critical-illness-section__form-text-field"
                  value={cwa.value}
                  onChange={e => dispatch(CIActions.changeCWA(e.target.value as string))}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  id="critical-illness-benefit-amt"
                  label="Benefit Amount"
                  variant="outlined"
                  placeholder="Enter Benefit Amount"
                  margin="normal"
                  fullWidth
                  className="critical-illness-section__form-text-field"
                  value={benefitAmount}
                  onChange={e => dispatch(CIActions.changeBenefitAmount(parseInt(e.target.value, 10)))}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl variant="outlined" className="critical-illness-section__form-text-field">
                  <InputLabel
                    shrink
                    ref={setRiderLabel}
                    id="critical-illness-ridercode1-label"
                    className={'critical-illness-section__form-text-field--label'}
                  >
                    Rider Code 1
                  </InputLabel>
                  <Select
                    labelId="critical-illness-label-ridercode1"
                    labelWidth={riderLabel?.clientWidth}
                    id="critical-illness-ridercode1"
                    value={riderCodeList.get(0) === undefined ? '' : riderCodeList.get(0)}
                    onChange={e => e.target.value === '' ? dispatch(CIActions.removeRider(0)) : dispatch(CIActions.changeRiderCodes(e.target.value as string, 0))}
                    displayEmpty
                  >
                    <MenuItem value="" key="None">
                      None
                </MenuItem>
                    {getAllRiderCodeUnits().map(rider => (
                      <MenuItem value={rider.code} key={rider.code}>
                        {rider.code}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl variant="outlined" className="critical-illness-section__form-text-field">
                  <InputLabel
                    shrink
                    ref={setRiderLabel}
                    id="critical-illness-rideramt1-label"
                    className={'critical-illness-section__form-text-field--label'}
                  >
                    Rider Amount 1
                  </InputLabel>
                  <Select
                    labelId="critical-illness-label-rideramt1"
                    labelWidth={riderLabel?.clientWidth}
                    id="critical-illness-rideramt1"
                    value={riderAmounts.get(0) === undefined ? '' : riderAmounts.get(0)}
                    onChange={e => dispatch(CIActions.changeRiderAmt(e.target.value as number, 0))}
                    displayEmpty
                    disabled={riderCodeList.get(0) !== undefined ? false : true}
                  >
                    {getRiderUnits(riderCodeList.get(0)).map(unit => (
                      <MenuItem value={unit} key={unit}>
                        {unit}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="checkedB"
                      color="secondary"
                      id="critical-illness-replacement"
                      checked={replacement}
                      onChange={e => {
                        setPolicyNumberBlurred(true);
                        dispatch(CIActions.changeReplacement(!replacement));
                      }}
                    />
                  }
                  label={<Typography className="case-details-section__checkbox-label">Replacement</Typography>}
                  className="critical-illness-section__form-text-field"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  id="critical-illness-policy-number"
                  label="Policy Number"
                  variant="outlined"
                  placeholder="Enter Policy Number"
                  margin="normal"
                  fullWidth
                  error={showPolicyNumberError}
                  helperText={showPolicyNumberError ? ErrorMessages.INVALID_POLICY_NUMBER : ''}
                  className="critical-illness-section__form-text-field"
                  value={policyNumber.key !== 'ValidPolicyNumber' || !replacement ? '' : policyNumber.value.value}
                  onBlur={() => setPolicyNumberBlurred(true)}
                  onChange={e => dispatch(CIActions.changePolicyNumber(e.target.value as string))}
                  disabled={!replacement}
                  inputProps={{
                    maxLength: 7,
                  }}
                />
              </Grid>
            </Grid>
          </div>
        </form>
      </div>
    </>
  );
};

export default NYCriticalIllness;
